import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BlockRenderer, type CmsBlock } from '../components/blocks/BlockRenderer';
import { headlessGet } from '../lib/api';

interface PreviewPageData {
  id: string;
  slug: string;
  title: string;
  metaTitle?: string | null;
  status: 'draft' | 'published';
  blocks: CmsBlock[];
}

type DeviceWidth = 375 | 768 | null;

const DEVICES = [
  { label: 'Mobile', width: 375 as DeviceWidth },
  { label: 'Tablet', width: 768 as DeviceWidth },
  { label: 'Desktop', width: null as DeviceWidth },
] as const;

const DEVICE_ICONS: Record<string, string> = {
  Mobile: 'M4 6a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z',
  Tablet:
    'M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  Desktop:
    'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
};

export function PreviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [page, setPage] = useState<PreviewPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceWidth, setDeviceWidth] = useState<DeviceWidth>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    headlessGet<PreviewPageData>(`/preview/${encodeURIComponent(token)}`)
      .then((data) => {
        setPage(data);
        document.title = `[Preview] ${data.metaTitle ?? data.title}`;
      })
      .catch((err: Error & { status?: number }) => {
        if (err.status === 410 || err.status === 401 || err.status === 403) {
          setError('This preview link has expired or is invalid.');
        } else if (err.status === 404) {
          setError('Preview page not found.');
        } else {
          setError('Failed to load preview. Please try again.');
        }
      })
      .finally(() => setLoading(false));

    return () => {
      document.title = '';
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <svg className="w-6 h-6 animate-spin text-zinc-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-zinc-50 px-4">
        <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-zinc-600 text-center max-w-sm">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="text-xs text-zinc-500 hover:text-zinc-800 underline"
        >
          Go to homepage
        </button>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Preview banner */}
      <div className="sticky top-0 z-50 flex items-center gap-3 bg-amber-400 px-4 py-2 text-xs font-medium text-amber-900">
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <span className="flex-1 truncate">
          Preview — &quot;{page.title}&quot; — not publicly visible
        </span>
        <div className="flex items-center gap-1 bg-amber-300/60 rounded-md px-1 py-0.5">
          {DEVICES.map(({ label, width }) => (
            <button
              key={label}
              onClick={() => setDeviceWidth(width)}
              title={width ? `${label} (${width}px)` : label}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] transition-colors ${
                deviceWidth === width
                  ? 'bg-amber-900 text-amber-50'
                  : 'hover:bg-amber-400/60'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={DEVICE_ICONS[label]} />
              </svg>
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div
        className="mx-auto transition-all duration-300 bg-white min-h-screen"
        style={deviceWidth ? { maxWidth: deviceWidth } : undefined}
      >
        <div className="space-y-6 py-6">
          {page.blocks
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((block) => (
              <BlockRenderer key={block.id} block={block} />
            ))}
          {page.blocks.length === 0 && (
            <div className="text-center py-24 text-zinc-400 text-sm px-4">
              This page has no content yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
